import type { SupabaseClient } from '@supabase/supabase-js'
import { parsePlanLimitError } from '../auth/planLimits'
import { getSupabaseClient } from '../supabase/client'
import { getSupabaseConfig } from '../supabase/config'
import { IMAGE_CONFIG, type NoteAsset, type RemoteNoteAsset } from './assetTypes'
import { assertWorkspaceActive, guardWorkspaceRequest, onWorkspaceRetired } from '../data/workspace'
import { APP_SETTINGS } from '../config/appSettings'

export interface AssetTransport {
  readonly id: string
  upload(asset: NoteAsset, userId: string): Promise<RemoteNoteAsset>
  pull(cursor: string | null): Promise<{ assets: RemoteNoteAsset[]; cursor: string }>
  pullForNote(noteId: string): Promise<RemoteNoteAsset[]>
  remove(asset: NoteAsset): Promise<void>
  getPreviewUrls(assets: NoteAsset[]): Promise<Map<string, string>>
}

interface AssetSyncRow {
  asset: RemoteNoteAsset
  server_updated_at?: string
  asset_id?: string
}

interface AssetPullCursor {
  after: string
  assetId: string
}

const INITIAL_ASSET_CURSOR: AssetPullCursor = {
  after: '1970-01-01T00:00:00.000Z',
  assetId: '00000000-0000-0000-0000-000000000000',
}

function decodeAssetCursor(cursor: string | null): AssetPullCursor {
  if (!cursor) return INITIAL_ASSET_CURSOR
  try {
    const value = JSON.parse(cursor) as Partial<AssetPullCursor>
    if (typeof value.after === 'string' && typeof value.assetId === 'string') {
      return { after: value.after, assetId: value.assetId }
    }
  } catch {
    // A malformed cursor safely falls back to a complete metadata pull.
  }
  return INITIAL_ASSET_CURSOR
}

function extensionForMimeType(mimeType: string): string {
  if (mimeType === 'image/png') return 'png'
  if (mimeType === 'image/jpeg') return 'jpg'
  if (mimeType === 'image/heic') return 'heic'
  if (mimeType === 'image/heif') return 'heif'
  return 'webp'
}

function storagePaths(asset: NoteAsset, userId: string, isDev: boolean) {
  const masterExtension = extensionForMimeType(asset.masterMimeType ?? asset.originalMimeType)
  const base = `${userId}/${isDev ? 'development' : 'production'}/${asset.noteId}/${asset.id}`
  return {
    master: `${base}/master.${masterExtension}`,
    thumbnail: asset.thumbnailBlob ? `${base}/thumb.webp` : null,
  }
}

async function uploadWithTus(
  blob: Blob,
  path: string,
  bucketName: string,
  endpoint: string,
  accessToken: string,
  contentType: string,
): Promise<void> {
  const { Upload } = await import('tus-js-client')
  guardWorkspaceRequest(endpoint, { headers: { authorization: `Bearer ${accessToken}` } })
  return new Promise((resolve, reject) => {
    let unsubscribe = () => {}
    const upload = new Upload(blob, {
      storeFingerprintForResuming: false,
      onBeforeRequest: async () => { guardWorkspaceRequest(endpoint, { headers: { authorization: `Bearer ${accessToken}` } }) },
      endpoint,
      retryDelays: [...APP_SETTINGS.images.resumableUploadRetryDelaysMs],
      headers: { authorization: `Bearer ${accessToken}`, 'x-upsert': 'false' },
      uploadDataDuringCreation: true,
      removeFingerprintOnSuccess: true,
      metadata: {
        bucketName,
        objectName: path,
        contentType,
        cacheControl: String(IMAGE_CONFIG.immutableCacheSeconds),
      },
      onError: error => { unsubscribe(); reject(error) },
      onSuccess: () => { unsubscribe(); try { assertWorkspaceActive(); resolve() } catch (error) { reject(error) } },
    })
    unsubscribe = onWorkspaceRetired(async () => {
      await upload.abort()
      unsubscribe()
      reject(new Error('Upload paused because the account workspace closed.'))
    })
    void upload.findPreviousUploads().then((previous) => {
      assertWorkspaceActive()
      const resumable = previous.find((entry) => entry.metadata.objectName === path)
      if (resumable) upload.resumeFromPreviousUpload(resumable)
      upload.start()
    }).catch(reject)
  })
}

function isAlreadyExistsError(error: { statusCode?: string; message?: string }): boolean {
  return error.statusCode === '409'
    || error.message?.toLowerCase().includes('already exists') === true
    || error.message?.toLowerCase().includes('duplicate') === true
}

export class SupabaseAssetTransport implements AssetTransport {
  readonly id: string = 'supabase'

  constructor(
    private readonly isDev: boolean,
    private readonly supabaseUrl: string,
    private readonly loadClient: () => Promise<SupabaseClient> = getSupabaseClient,
  ) {}

  async upload(asset: NoteAsset, userId: string): Promise<RemoteNoteAsset> {
    const master = asset.processedMasterBlob ?? asset.sourceBlob
    if (!master) throw new Error('The local image data is unavailable.')
    const client = await this.loadClient()
    const paths = storagePaths(asset, userId, this.isDev)
    await this.uploadBlob(client, master, paths.master, asset.masterMimeType ?? master.type)
    if (asset.thumbnailBlob && paths.thumbnail) {
      await this.uploadBlob(
        client,
        asset.thumbnailBlob,
        paths.thumbnail,
        asset.thumbnailMimeType ?? asset.thumbnailBlob.type,
      )
    }

    const remote: RemoteNoteAsset = {
      id: asset.id,
      noteId: asset.noteId,
      kind: 'image',
      originalMimeType: asset.originalMimeType,
      masterMimeType: asset.masterMimeType ?? master.type,
      thumbnailMimeType: asset.thumbnailMimeType,
      width: asset.width,
      height: asset.height,
      originalSize: asset.originalSize,
      processedSize: master.size,
      masterStoragePath: paths.master,
      thumbnailStoragePath: paths.thumbnail,
      state: 'synced',
      createdAt: asset.createdAt,
      modifiedAt: Date.now(),
    }
    const { data, error } = await client.rpc('sync_note_asset', {
      p_is_dev: this.isDev,
      p_asset_id: remote.id,
      p_note_id: remote.noteId,
      p_payload: remote,
      p_modified_at: remote.modifiedAt,
    })
    if (error) throw parsePlanLimitError(error) ?? error
    const row = (data as AssetSyncRow[] | null)?.[0]
    if (!row?.asset) throw new Error('Supabase returned no synchronized asset.')
    return row.asset
  }

  async pull(cursor: string | null): Promise<{ assets: RemoteNoteAsset[]; cursor: string }> {
    const current = decodeAssetCursor(cursor)
    const client = await this.loadClient()
    const { data, error } = await client.rpc('pull_note_assets', {
      p_is_dev: this.isDev,
      p_after: current.after,
      p_after_asset_id: current.assetId,
      p_limit: APP_SETTINGS.sync.pullPageSize,
    })
    if (error) throw error
    const rows = (data ?? []) as AssetSyncRow[]
    const last = rows.at(-1)
    return {
      assets: rows.map((row) => row.asset),
      cursor: last?.server_updated_at && last.asset_id
        ? JSON.stringify({ after: last.server_updated_at, assetId: last.asset_id })
        : JSON.stringify(current),
    }
  }

  async pullForNote(noteId: string): Promise<RemoteNoteAsset[]> {
    const client = await this.loadClient()
    const { data, error } = await client.rpc('get_note_assets', {
      p_is_dev: this.isDev,
      p_note_id: noteId,
    })
    if (error) throw error
    return ((data ?? []) as AssetSyncRow[]).map((row) => row.asset)
  }

  async remove(asset: NoteAsset): Promise<void> {
    const client = await this.loadClient()
    const paths = [asset.masterStoragePath, asset.thumbnailStoragePath]
      .filter((path): path is string => Boolean(path))
    if (paths.length > 0) {
      const { error } = await client.storage.from(IMAGE_CONFIG.storageBucket).remove(paths)
      if (error) throw error
    }
    const { error } = await client.rpc('delete_note_asset', {
      p_is_dev: this.isDev,
      p_asset_id: asset.id,
      p_modified_at: asset.modifiedAt,
    })
    if (error) throw error
  }

  async getPreviewUrls(assets: NoteAsset[]): Promise<Map<string, string>> {
    const candidates = assets.flatMap((asset) => {
      const path = asset.thumbnailStoragePath ?? asset.masterStoragePath
      return path ? [{ asset, path }] : []
    })
    if (candidates.length === 0) return new Map()
    const client = await this.loadClient()
    const { data, error } = await client.storage
      .from(IMAGE_CONFIG.storageBucket)
      .createSignedUrls(candidates.map(({ path }) => path), APP_SETTINGS.images.signedUrlTtlSeconds)
    if (error) throw error
    const urls = new Map<string, string>()
    candidates.forEach(({ asset }, index) => {
      const signedUrl = data[index]?.signedUrl
      if (signedUrl) urls.set(asset.id, signedUrl)
    })
    return urls
  }

  protected async uploadBlob(
    client: SupabaseClient,
    blob: Blob,
    path: string,
    contentType: string,
  ): Promise<void> {
    if (blob.size > IMAGE_CONFIG.standardUploadMaxBytes) {
      const { data, error } = await client.auth.getSession()
      if (error) throw error
      const accessToken = data.session?.access_token
      if (!accessToken) throw new Error('Sign in before synchronizing images.')
      await uploadWithTus(
        blob,
        path,
        IMAGE_CONFIG.storageBucket,
        `${this.supabaseUrl}/storage/v1/upload/resumable`,
        accessToken,
        contentType,
      )
      return
    }

    const { error } = await client.storage.from(IMAGE_CONFIG.storageBucket).upload(path, blob, {
      contentType,
      cacheControl: String(IMAGE_CONFIG.immutableCacheSeconds),
      upsert: false,
    })
    if (error && !isAlreadyExistsError(error)) throw error
  }
}

/**
 * The Neon backend keeps the same metadata path (the sync_note_asset / pull RPCs, through the Neon data client) and replaces only the files: they go to
 * the App Worker's /api/images, which stores them in R2. Whatever the size, a file goes up in one request: the resumable upload was a Supabase Storage feature,
 * and an interrupted upload is retried by the asset synchronizer like any other failed attempt.
 */
export class NeonAssetTransport extends SupabaseAssetTransport {
  override readonly id: string = 'neon'

  protected override async uploadBlob(client: SupabaseClient, blob: Blob, path: string, contentType: string): Promise<void> {
    const { error } = await client.storage.from(IMAGE_CONFIG.storageBucket).upload(path, blob, {
      contentType,
      cacheControl: String(IMAGE_CONFIG.immutableCacheSeconds),
      upsert: false,
    })
    if (error) throw error
  }
}

export function createAssetTransport(): AssetTransport | null {
  const config = getSupabaseConfig()
  if (!config) return null
  return config.backend === 'neon' ? new NeonAssetTransport(config.isDev, config.url) : new SupabaseAssetTransport(config.isDev, config.url)
}
